import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import { useIntl } from 'react-intl';

import { CheckOutlined } from '@ant-design/icons';
import { RegistrationLabel } from '~src/services/api/labels';

import './styles.css';

interface FieldSelectorProps extends Partial<SelectProps<any>> {
  data: RegistrationLabel[];
  defaultSelected?: Record<number, number[]>;
  selected?: Record<number, number[]>;
  selectedGroups?: (data: Record<number, number[]>) => void;
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
  options,
  selectedGroups,
  defaultSelected = {},
  selected,
  data = [],
  ...selectProps
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Internal state, but kept in sync with parent when `selected` is provided
  const [selectedValues, setSelectedValues] = useState<
    Record<number, number[]>
  >(selected ?? defaultSelected);

  const intl = useIntl();

  useEffect(() => {
    setSelectedValues(selected ?? defaultSelected);
  }, [selected, defaultSelected]);

  const handleGroupSelect = (
    groupKey: number,
    groupOptions: RegistrationLabel['labels']
  ) => {
    const allSelected = groupOptions.every((option) =>
      selectedValues[groupKey]?.includes(option.id)
    );

    const newValues = allSelected
      ? []
      : groupOptions.map((option) => option.id);

    // Keep previous behavior: don't allow removing defaults by group-toggle
    const combinedValues = Array.from(
      new Set([...(defaultSelected[groupKey] || []), ...newValues])
    );

    setSelectedValues((prev) => {
      const values = { ...prev, [groupKey]: combinedValues };
      selectedGroups && selectedGroups(values);
      return values;
    });
  };

  const handleSingleSelect = (groupKey: number, optionKey: number) => {
    const isSelected = selectedValues[groupKey]?.includes(optionKey);

    if (isSelected && defaultSelected[groupKey]?.includes(optionKey)) {
      return; // Do nothing if trying to deselect a default selected value
    }

    setSelectedValues((prev) => {
      const values = {
        ...prev,
        [groupKey]: isSelected
          ? prev[groupKey]?.filter((value) => value !== optionKey)
          : [...(prev[groupKey] || []), optionKey],
      };

      selectedGroups && selectedGroups(values);
      return values;
    });
  };

  return (
    <Select
      className="borderless"
      style={{ width: '100%' }}
      mode="tags"
      open={isOpen}
      placeholder={intl.formatMessage({ id: 'labels-choose' })}
      onDropdownVisibleChange={(isItOpen) => {
        if (isOpen) {
          setIsOpen(isItOpen);
        }
      }}
      onClick={() => setIsOpen((prevState) => !prevState)}
      optionFilterProp="label"
      dropdownMatchSelectWidth={false}
      maxTagCount={3}
      maxTagPlaceholder={(hidden) => `+${hidden.length}`}
      maxTagTextLength={5}
      options={options}
      dropdownRender={() => {
        return (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {data.map((optionRoot) => {
              const allInGroupSelected = optionRoot.labels.every((option) =>
                selectedValues[optionRoot.id]?.includes(option.id)
              );

              return (
                <div className={'group-item'} key={optionRoot.id}>
                  <div
                    className={'group-label'}
                    style={
                      allInGroupSelected
                        ? {
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 700,
                            background: '#e6f7ff',
                          }
                        : { display: 'flex', alignItems: 'center' }
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGroupSelect(optionRoot.id, optionRoot.labels);
                    }}>
                    {optionRoot.name}
                    {allInGroupSelected && (
                      <CheckOutlined rev style={{ marginRight: '18px' }} />
                    )}
                  </div>

                  <div className={'sub-options'}>
                    {optionRoot.labels.map(({ title, id }) => {
                      const isItSelected = selectedValues[
                        optionRoot.id
                      ]?.includes(id);

                      return (
                        <div
                          key={id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSingleSelect(optionRoot.id, id);
                          }}
                          className={'sub-option'}>
                          {title}
                          {isItSelected && (
                            <CheckOutlined
                              rev
                              style={{ marginRight: '18px' }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }}
      {...selectProps}
    />
  );
};
